import { useForm, SubmitHandler } from 'react-hook-form';
import { Input, Button, FormControl, FormLabel, Text, Box, useToast, Heading } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { UIErrorResponse } from '../../../../../packages/types/src/error';
import { sendForgotPasswordLink } from '../../apis/auth';
import { useState } from 'react';

interface ForgotPasswordFormInputs {
	email: string;
}

interface ForgotPasswordFormProps {
	onSignInClick: () => void;
}

const ForgotPasswordForm = ({ onSignInClick }: ForgotPasswordFormProps) => {
	const toast = useToast();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ForgotPasswordFormInputs>();

	const [isForgotPasswordSuccess, setIsForgotPasswordSuccess] = useState(false);

	const { mutate: forgotPasswordMutate, isLoading: isForgotPasswordLoading } = useMutation(sendForgotPasswordLink, {
		onSuccess: () => {
			setIsForgotPasswordSuccess(true);
			toast({
				title: 'Success',
				description: 'Password reset link sent to your email',
				status: 'success',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
		onError: (error: AxiosError<UIErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'Password reset failed',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});

	const onSubmit: SubmitHandler<ForgotPasswordFormInputs> = data => {
		forgotPasswordMutate({ email: data.email });
	};

	return (
		<Box>
			{isForgotPasswordSuccess ? (
				<Heading color="#5968D8" textAlign={'center'} mx="auto" as="h5">
					Reset password link sent to your email
				</Heading>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormControl id="email" isInvalid={errors.email ? true : false}>
						<Text as={FormLabel} textStyle="body2-md">
							Email
						</Text>
						<Input placeholder="name@mail.com" type="email" {...register('email', { required: 'Email is required' })} />
						{errors.email && (
							<Text textStyle="body2" mt="6px" color="#D61E27">
								{errors.email.message}
							</Text>
						)}
					</FormControl>
					<Button mt="24px" type="submit" variant="primary" size="lg" w="full">
						Submit
					</Button>
					<Button
						onClick={
							onSignInClick
								? e => {
										e.preventDefault();
										onSignInClick();
								  }
								: undefined
						}
						isLoading={isForgotPasswordLoading}
						as={RouterLink}
						to="/sign-in"
						mt="8px"
						variant="secondary-grey"
						size="lg"
						w="full"
					>
						Cancel
					</Button>
				</form>
			)}
		</Box>
	);
};

export default ForgotPasswordForm;
